
import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import config from '../config/config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = () => {
      if (token) {
        axios.defaults.headers.common['x-auth-token'] = token;
        try {
          const decoded = jwtDecode(token);
          const currentTime = Date.now() / 1000;

          if (decoded.exp < currentTime) {
            console.log("Token expired. Logging out.");
            logout();
          } else {
            setUser({ isAuthenticated: true, ...decoded.user });
          }
        } catch (error) {
          console.error("Invalid token", error);
          setToken(null);
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          setUser(null);
        }
      } else {
        delete axios.defaults.headers.common['x-auth-token'];
        setUser(null);
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  const login = async (email, password, rememberMe = false) => {
    try {
      const res = await axios.post(`${config.backendUrl}/api/auth/login`, { email, password });
      const { token } = res.data;
      setToken(token);
      if (rememberMe) {
        localStorage.setItem('token', token);
      } else {
        sessionStorage.setItem('token', token);
      }
      const decoded = jwtDecode(token);
      setUser({ isAuthenticated: true, ...decoded.user });
      return true;
    } catch (err) {
      console.error(err.response.data);
      return false;
    }
  };

  const register = async (name, email, password) => {
    try {
      const res = await axios.post(`${config.backendUrl}/api/auth/register`, { name, email, password });
      setToken(res.data.token);
      // For registration, we can default to session storage
      sessionStorage.setItem('token', res.data.token);
      const decoded = jwtDecode(res.data.token);
      setUser({ isAuthenticated: true, ...decoded.user });
      return true;
    } catch (err) {
      console.error(err.response.data);
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (newToken) => {
    setToken(newToken);
    // When updating token, we need to know where the original token was stored.
    // We'll check localStorage first, then sessionStorage.
    if (localStorage.getItem('token')) {
      localStorage.setItem('token', newToken);
    } else {
      sessionStorage.setItem('token', newToken);
    }
    const decoded = jwtDecode(newToken);
    setUser({ isAuthenticated: true, ...decoded.user });
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
